import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, FolderKanban, ArrowRight, CheckCircle2, Rocket, Layers } from "lucide-react";
import { AccountShell } from "../../components/account/AccountShell";
import { useAuth } from "../../hooks/useAuth";
import { getAccountProjects } from "../../lib/api";
import { fmtDate } from "../../lib/format";

const TYPE_ICON = { preview_project: Rocket, continue_building: Layers };

export default function AccountProjects() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    if (!user) return;
    setLoading(true);
    getAccountProjects()
      .then(setProjects)
      .catch(() => setProjects([]))
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <AccountShell
      title="My Projects"
      subtitle="Your completed projects, kept here permanently for reference."
      testid="account-projects-page"
    >
      {loading ? (
        <div className="flex items-center justify-center py-20 text-[#687280]">
          <Loader2 className="animate-spin" size={22} />
        </div>
      ) : projects.length === 0 ? (
        <div className="bg-[#F7F7F7] rounded-2xl p-10 text-center" data-testid="no-projects-state">
          <div className="w-12 h-12 rounded-xl bg-white text-[#D4AF37] flex items-center justify-center mx-auto mb-4">
            <FolderKanban size={22} />
          </div>
          <p className="font-display font-semibold text-[#0F0F0F]">No completed projects yet</p>
          <p className="text-sm text-[#687280] mt-1">
            Once a project is completed, it will appear here for you to revisit anytime.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="projects-grid">
          {projects.map((p) => {
            const Icon = TYPE_ICON[p.type] || FolderKanban;
            return (
              <div
                key={p.id}
                className="bg-white rounded-2xl p-6 border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex flex-col"
                data-testid={`project-card-${p.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-[#F7F7F7] text-[#D4AF37] flex items-center justify-center shrink-0">
                      <Icon size={18} />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-display text-lg font-bold text-[#0F0F0F] truncate">{p.name}</h3>
                      <p className="text-xs text-[#687280]">{p.category}</p>
                    </div>
                  </div>
                  <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-600 px-2.5 py-0.5 text-xs font-semibold">
                    <CheckCircle2 size={12} /> {p.status}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-3 mt-5 text-sm">
                  <div>
                    <p className="text-[#9CA3AF] text-xs">Started</p>
                    <p className="font-semibold text-[#0F0F0F]">{fmtDate(p.date_started)}</p>
                  </div>
                  <div>
                    <p className="text-[#9CA3AF] text-xs">Last updated</p>
                    <p className="font-semibold text-[#0F0F0F]">{fmtDate(p.last_updated)}</p>
                  </div>
                </div>

                <button
                  onClick={() => navigate(p.link)}
                  className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-[#0F0F0F] text-white py-2.5 text-sm font-semibold hover:bg-[#1c1c1c] transition-colors"
                  data-testid={`view-project-${p.id}`}
                >
                  View Project <ArrowRight size={15} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </AccountShell>
  );
}
